import React, { useRef } from 'react';
import { cn } from '@rocco/utils/cn';

export type CategoryToggleValue = 'interior' | 'building';

interface CategoryToggleProps {
    activeToggle: CategoryToggleValue;
    onToggleChange: (toggle: CategoryToggleValue) => void;
}

/**
 * Toggle between Interior & Building used in the CategoryNavigationBar.
 * @param activeToggle - The active toggle value.
 * @param onToggleChange - The function to call when the toggle value changes.
 */
export const CategoryToggle = ({
    activeToggle,
    onToggleChange,
}: CategoryToggleProps) => {
    const interiorButtonRef = useRef<HTMLButtonElement>(null);
    const buildingButtonRef = useRef<HTMLButtonElement>(null);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const newValue: CategoryToggleValue =
                e.key === 'ArrowLeft' ? 'interior' : 'building';
            onToggleChange(newValue);

            // Move focus to the newly selected button
            if (newValue === 'interior') {
                interiorButtonRef.current?.focus();
            } else {
                buildingButtonRef.current?.focus();
            }
        }
    };

    return (
        // Outer wrapper with negative margin to compensate for shadow padding
        <div className="-my-4 shrink-0 py-4">
            <div
                role="group"
                aria-label="Category type"
                onKeyDown={handleKeyDown}
                className="relative flex h-10.5 w-42.5 items-center rounded-full border border-black"
            >
                {/* Sliding highlight background with prominent shadow */}
                <div
                    aria-hidden="true"
                    className={cn(
                        'absolute -top-px -bottom-px left-0 w-[calc(50%+0.25rem)] rounded-full bg-[#323232] shadow-[0px_4px_24px_0px_rgba(0,0,0,0.34)] transition-all duration-300',
                        activeToggle === 'building'
                            ? 'translate-x-[calc(100%-0.5rem)]'
                            : 'translate-x-0',
                    )}
                />

                <button
                    ref={interiorButtonRef}
                    type="button"
                    aria-pressed={activeToggle === 'interior'}
                    onClick={() => onToggleChange('interior')}
                    className={cn(
                        'relative z-10 -mr-1 w-1/2 rounded-full py-2 text-base transition-colors',
                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                        activeToggle === 'interior'
                            ? 'text-white'
                            : 'text-[#323232]',
                    )}
                >
                    Interior
                </button>

                <button
                    ref={buildingButtonRef}
                    type="button"
                    aria-pressed={activeToggle === 'building'}
                    onClick={() => onToggleChange('building')}
                    className={cn(
                        'relative z-10 w-1/2 rounded-full py-2 text-base transition-colors',
                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                        activeToggle === 'building'
                            ? 'text-white'
                            : 'text-[#323232]',
                    )}
                >
                    Building
                </button>
            </div>
        </div>
    );
};
